<script setup lang="ts">
  import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'

  import { SearchSmIcon } from '@gohighlevel/ghl-icons/24/outline'

  import { DEFAULT_IMAGES } from '@common/core-layer/constants'
  import SmartImage from '@common/media-layer/components/SmartImage.vue'

  import { useSharedIntersectionObserver } from '@communities/posts-layer/composables/useSharedIntersectionObserver'

  import CPInput from '@ui/components/CPInput.vue'
  import CPText from '@ui/components/CPText.vue'

  import { useChats } from '../../composables/useChats'
  import ConversationCardShimmer from '../atoms/ConversationCardShimmer.vue'
  import MemberSuggestionItem from '../atoms/MemberSuggestionItem.vue'

  import type { IConversation, IConversationSuggestion } from '../../types/conversation.types'

  const emit = defineEmits<{
    conversationCreated: [conversation: IConversation]
  }>()

  defineOptions({
    name: 'NewChatView',
  })

  // Composables
  const { t } = useI18n()
  const chats = useChats()

  // Local state
  const inputValue = ref('') // Immediate display value
  const searchQuery = ref('') // Debounced query for API calls
  const loadingContactId = ref<string | null>(null)
  const loadMoreTriggerRef = ref<HTMLElement | null>(null)
  const noSearchResultsImage = DEFAULT_IMAGES.COMMUNITIES.CHATS_NO_SEARCH_RESULTS

  // Search for suggestions based on debounced search query
  const suggestionsQuery = chats.searchSuggestions(searchQuery)

  // Flatten paginated data
  const suggestions = computed(() => {
    return suggestionsQuery.data.value?.pages.flatMap(page => page.suggestions) || []
  })

  const isLoading = computed(() => suggestionsQuery.isLoading.value)
  const isFetchingNextPage = computed(() => suggestionsQuery.isFetchingNextPage.value)
  const hasNextPage = computed(() => suggestionsQuery.hasNextPage?.value ?? false)

  const sharedObserver = useSharedIntersectionObserver({
    root: null,
    rootMargin: '100px',
    threshold: 0.1,
  })

  // Handle contact selection - create conversation and navigate
  const handleContactClick = async (contact: IConversationSuggestion) => {
    try {
      loadingContactId.value = contact.contactId

      // Create conversation with this contact
      const conversation = await chats.startChatWithContact(contact)

      // Switch back to conversations view
      chats.store.showConversationsView()

      // Emit event with the new conversation
      emit('conversationCreated', conversation)
    } catch (error) {
      console.error('Failed to create conversation:', error)
    } finally {
      loadingContactId.value = null
    }
  }

  // Debounce search query for API calls
  let searchTimeout: ReturnType<typeof setTimeout> | null = null

  // Watch inputValue and debounce searchQuery updates
  watch(inputValue, newValue => {
    if (searchTimeout) {
      clearTimeout(searchTimeout)
    }
    searchTimeout = setTimeout(() => {
      searchQuery.value = newValue
    }, 300)
  })

  // Setup intersection observer for infinite scroll
  const setupIntersectionObserver = () => {
    if (!loadMoreTriggerRef.value) return

    sharedObserver.disconnect()
    sharedObserver.observe(loadMoreTriggerRef.value, entry => {
      if (entry.isIntersecting && hasNextPage.value && !isFetchingNextPage.value && !isLoading.value) {
        suggestionsQuery.fetchNextPage()
      }
    })
  }

  // Watch for changes in suggestions to re-register observer
  watch(
    () => [suggestions.value.length, hasNextPage.value],
    () => {
      nextTick(() => {
        setupIntersectionObserver()
      })
    }
  )

  onMounted(() => {
    nextTick(() => {
      setupIntersectionObserver()
    })
  })

  onUnmounted(() => {
    sharedObserver.disconnect()
    if (searchTimeout) {
      clearTimeout(searchTimeout)
    }
  })
</script>

<template>
  <div class="flex h-full min-h-0 flex-col">
    <!-- Search input -->
    <div class="flex flex-col gap-2 px-4 py-3">
      <CPText size="sm" class="text-secondary">
        {{ t('chats.newChat.messageTo') }}
      </CPText>
      <CPInput
        id="new-chat-search"
        :model-value="inputValue"
        :placeholder="t('chats.newChat.searchPlaceholder')"
        size="sm"
        class="w-full"
        @update:model-value="inputValue = $event"
      >
        <template #prefix>
          <SearchSmIcon class="h-4 w-4 text-secondary" />
        </template>
      </CPInput>
    </div>

    <!-- Suggestions list -->
    <div class="custom-scrollbar flex-1 min-h-0 overflow-y-auto overscroll-contain" @wheel.stop @touchmove.stop>
      <!-- Loading state -->
      <div v-if="isLoading && suggestions.length === 0" class="flex flex-col">
        <ConversationCardShimmer v-for="i in 5" :id="`new-chat-shimmer-${i}`" :key="`shimmer-${i}`" />
      </div>

      <!-- Empty state - No search results -->
      <div
        v-else-if="suggestions.length === 0 && !isLoading"
        class="flex h-full flex-col items-center justify-center px-6 py-12 text-center"
      >
        <!-- Illustration -->
        <div class="mb-4 h-32 w-32">
          <SmartImage
            :src="noSearchResultsImage"
            :alt="t('chats.images.altNoSearchResults')"
            :width="128"
            :height="128"
            :quality="100"
            format="webp"
            class="h-full w-full"
          />
        </div>

        <CPText size="2xl" weight="semibold" class="mb-2 text-body">
          {{ t('chats.emptyState.noSearchResults') }}
        </CPText>
        <CPText size="lg" class="text-secondary">
          {{ t('chats.emptyState.noSearchResultsDescription') }}
        </CPText>
      </div>

      <!-- Suggestions -->
      <div v-else class="flex flex-col">
        <div v-for="(contact, index) in suggestions" :key="contact.contactId">
          <MemberSuggestionItem :contact="contact" :is-loading="loadingContactId === contact.contactId" @click="handleContactClick" />
          <!-- Divider -->
          <div v-if="index < suggestions.length - 1" class="mx-4 border-b border-default" />
        </div>

        <!-- Load more trigger -->
        <div id="new-chat-load-more-trigger" ref="loadMoreTriggerRef" class="h-4" />

        <!-- Loading more -->
        <template v-if="isFetchingNextPage">
          <div class="mx-4 border-b border-default" />
          <ConversationCardShimmer id="new-chat-shimmer-next-1" />
          <div class="mx-4 border-b border-default" />
          <ConversationCardShimmer id="new-chat-shimmer-next-2" />
        </template>
      </div>
    </div>
  </div>
</template>

<style scoped>
  .custom-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: var(--cp-font-300) transparent;
  }

  .custom-scrollbar::-webkit-scrollbar {
    width: 3px;
  }

  .custom-scrollbar::-webkit-scrollbar-track {
    background: transparent;
  }

  .custom-scrollbar::-webkit-scrollbar-thumb {
    background-color: var(--cp-font-300);
    border-radius: 2px;
  }

  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background-color: var(--cp-font-400);
  }
</style>
